import React, { Suspense } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import styled from 'styled-components'
import Header from '../components/Header'
import Polling from '../components/Header/Polling'
import Popups from '../components/Popups'

import DarkModeQueryParamReader from '../theme/DarkModeQueryParamReader'
import { RedirectDuplicateTokenIds } from './AddLiquidity/redirects'

import MigrateV2 from './MigrateV2'
import MigrateV2Pair from './MigrateV2/MigrateV2Pair'
import Pool from './Pool'
import PoolFinder from './PoolFinder'
import RemoveLiquidity from './RemoveLiquidity'
import RemoveLiquidityV3 from './RemoveLiquidity/V3'
import Swap from './Swap'
import { RedirectPathToSwapOnly, RedirectToSwap } from './Swap/redirects'

import { RedirectDuplicateTokenIdsV2 } from './AddLiquidityV2/redirects'
import { PositionPage } from './Pool/PositionPage'
import AddLiquidity from './AddLiquidity'
import { ThemedBackground } from '../theme'
import FarmList from './FarmList'
import FarmPool from './FarmPool'
import RedeemXHSPX from './RedeemXHSPX'
import StakeList from './StakeList'
import Footer from 'components/Footer'
import RootWrapper from './utils/RootWrapper'
import Stake from './Stake'
import LiquidityHub from './LiquidityHub'
import Testnet from './Testnet'
import ExplorePools from './ExplorePools'
import Launcher from './Launcher'

import DisclaimerModal from 'components/DisclaimerModal'
import { MAINTENANCE_IS_ACTIVATED } from 'constants/config'
import MaintenancePage from './MaintenancePage'
import DiscordModal from 'components/DiscordModal'
import VC from './VC'
import { isLauncherSubdomain, isRegisterSubdomain, isVCSubdomain } from 'utils/data'
import ErrorPage from './ErrorPage'
import * as Sentry from '@sentry/react'
import useScrollToTop from 'hooks/useScrollToTop'
import ReferralHandler from './utils/ReferralHandler'
import AddyHandler from './utils/AddyHandler'
import SignReferral from './SignReferral'
import Delegator from './Delegator'
import RemoveLiquidityEmergency from './RemoveLiquidity/EmergencyV2'
import EmergencyRemoveLiquidityV3 from './RemoveLiquidity/EmergencyV3'
import { EmergencyPositionPageV3 } from './Pool/EmergencyPositionPageV3'
import EcosystemPoints from './EcosystemPoints'

const AppWrapper = styled.div`
  display: flex;
  flex-flow: column;
  align-items: flex-start;
`

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 120px;
  align-items: center;
  flex: 1;
  z-index: 1;
  transition: all ease 500ms;

  animation: fadeUp 0.5s ease-out;

  @keyframes fadeUp {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${({ theme }) => theme.mediaWidth.upToSmall`
    padding: 16px;
    padding-top: 6rem;
  `};
`

const HeaderWrapper = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  width: 100%;
  justify-content: space-between;
  position: fixed;
  top: 0;
  z-index: 2;
`

const Marginer = styled.div`
  margin-top: 5rem;
`

export default function App() {
  useScrollToTop()
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)

    const isDev = urlParams.get('IS_ADMIN') === 'true'

    if (isDev) {
      localStorage.setItem('SKIP_MAINTENANCE_STATUS', 'true')
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [])

  const skipMaintenance = localStorage.getItem('SKIP_MAINTENANCE_STATUS') === 'true'

  if (MAINTENANCE_IS_ACTIVATED && !skipMaintenance) {
    return <MaintenancePage />
  }

  return (
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
      <Suspense fallback={null}>
        <Route component={DarkModeQueryParamReader} />
        <RootWrapper>
          <ReferralHandler />
          <AddyHandler />
          <DisclaimerModal />

          <DiscordModal />

          <AppWrapper>
            <HeaderWrapper>
              <Header />
            </HeaderWrapper>
            <BodyWrapper>
              <ThemedBackground />
              <Popups />
              <Polling />

              <Switch>
                <Route exact strict path="/send" component={RedirectPathToSwapOnly} />
                <Route exact strict path="/swap/:outputCurrency" component={RedirectToSwap} />
                <Route exact strict path="/swap" component={Swap} />

                <Route exact strict path="/find" component={PoolFinder} />
                <Route exact strict path="/pool/v2" component={Pool} />
                <Route exact strict path="/pool" component={Pool} />
                <Route exact strict path="/pool/:tokenId" component={PositionPage} />

                <Route exact strict path="/farm" component={FarmList} />
                <Route exact strict path="/farm/:lptoken" component={FarmPool} />

                <Route exact strict path="/stake" component={StakeList} />
                <Route exact strict path="/stake/:id" component={Stake} />

                <Route exact strict path="/redeem" component={RedeemXHSPX} />

                <Route exact strict path="/liquidity-hub" component={LiquidityHub} />
                <Route exact strict path="/explore/explore-pools" component={ExplorePools} />
                <Route exact strict path="/testnet" component={Testnet} />
                <Route exact strict path="/register" component={SignReferral} />
                <Route
                  exact
                  strict
                  path="/add/v2/:currencyIdA?/:currencyIdB?"
                  component={RedirectDuplicateTokenIdsV2}
                />

                <Route
                  exact
                  strict
                  path="/add/:currencyIdA?/:currencyIdB?/:feeAmount?"
                  component={RedirectDuplicateTokenIds}
                />

                <Route
                  exact
                  strict
                  path="/increase/:currencyIdA?/:currencyIdB?/:feeAmount?/:tokenId?"
                  component={AddLiquidity}
                />

                <Route exact strict path="/remove/v2/:currencyIdA/:currencyIdB" component={RemoveLiquidity} />
                <Route
                  exact
                  strict
                  path="/emergency-remove/v2/:currencyIdA/:currencyIdB"
                  component={RemoveLiquidityEmergency}
                />

                <Route exact strict path="/remove/:tokenId" component={RemoveLiquidityV3} />
                <Route exact strict path="/emergency-remove/:tokenId" component={EmergencyRemoveLiquidityV3} />
                <Route exact strict path="/emergency-pool/:tokenId" component={EmergencyPositionPageV3} />
                <Route exact strict path="/migrate/v2" component={MigrateV2} />
                <Route exact strict path="/migrate/v2/:address" component={MigrateV2Pair} />

                <Route exact strict path="/ecosystem-points" component={EcosystemPoints} />

                <Route exact strict path="/vc-dashboard" component={VC} />
                <Route exact strict path="/launcher" component={Launcher} />
                <Route exact strict path="/delegator" component={Delegator} />

                {isRegisterSubdomain() && <Redirect to="/register" />}
                {isLauncherSubdomain() && <Redirect to="/launcher" />}
                {isVCSubdomain() && <Redirect to="/vc-dashboard" />}
                <Route component={RedirectPathToSwapOnly} />
              </Switch>
              <Marginer />
            </BodyWrapper>
          </AppWrapper>
        </RootWrapper>
        <Footer />
      </Suspense>
    </Sentry.ErrorBoundary>
  )
}
